import { ReactNode } from "react";
import { FieldValues, SubmitHandler, UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { AlertCircle } from "lucide-react";
import { FormBase } from "@core/features/form";
import { Type } from "@themes/pollinator/ui/Type";

export type Props<T extends FieldValues> = {
  /** Form instance from useForm hook */
  form: UseFormReturn<T>;
  /** Form content - typically FormField components */
  children: ReactNode;
  /** Submit handler */
  onSubmit?: SubmitHandler<T>;
  /** Additional CSS classes for form element */
  className?: string;
  /** Additional CSS classes for inner content wrapper */
  innerClassName?: string;
  /** Additional CSS classes for error container */
  errorContainerClassName?: string;
};

/**
 * Styled form wrapper that provides react-hook-form context and handles root errors.
 * Wraps FormBase with styled error display.
 *
 * @example
 * const { form, handleSubmit } = useForm({ schema });
 *
 * <Form form={form} onSubmit={handleSubmit}>
 *   <FormField name="email" label="Email" component="Input" />
 *   <FormField name="password" label="Password" component="Input" type="password" />
 *   <Button type="submit">Submit</Button>
 * </Form>
 */
export function Form<T extends FieldValues>({
  form,
  children,
  onSubmit,
  className,
  innerClassName,
  errorContainerClassName,
}: Props<T>) {
  const { isSubmitting } = form.formState;

  return (
    <FormBase
      form={form}
      onSubmit={onSubmit}
      className={twMerge("relative flex flex-col", className)}
      renderInner={({ children: innerChildren }) => (
        <div
          className={twMerge(
            "flex-1 flex flex-col gap-4 transition-opacity duration-200",
            innerClassName,
            isSubmitting && "opacity-50"
          )}
        >
          {innerChildren}
        </div>
      )}
      renderRootError={({ message }) => (
        <div
          className={twMerge(
            "w-full mx-auto flex flex-col max-w-xl mt-8",
            errorContainerClassName
          )}
        >
          <div className="flex flex-col gap-2 border-2 border-error rounded-md p-4">
            <div className="flex flow-row items-center gap-2">
              <AlertCircle className="h-5 w-5 text-error" />
              <Type variant="h6" className="text-error">
                Something went wrong
              </Type>
            </div>
            <Type variant="caption" className="text-error">
              {message}
            </Type>
          </div>
        </div>
      )}
    >
      {children}
    </FormBase>
  );
}

Form.displayName = "Form";

export default Form;

